
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { startOfMonth, endOfMonth, format } from "date-fns";

interface AdminMetricsData {
  usersCount: number;
  policiesCount: number;
  totalPremiums: number;
  estimatedCommissions: number;
}

export const useAdminMetrics = () => {
  return useQuery<AdminMetricsData>({
    queryKey: ['adminMetrics'],
    queryFn: async () => {
      try {
        // Current month date range (April 2025)
        const currentDate = new Date();
        const startDate = startOfMonth(currentDate);
        const endDate = endOfMonth(currentDate);
        
        // Format dates for the query
        const startDateStr = format(startDate, 'yyyy-MM-dd');
        const endDateStr = format(endDate, 'yyyy-MM-dd');
        
        // Fetch counts
        const { count: usersCount } = await supabase
          .from('profiles')
          .select('*', { count: 'exact', head: true });

        const { count: policiesCount } = await supabase
          .from('policies')
          .select('*', { count: 'exact', head: true });
        
        // Fetch sum of premium_amount for the current month
        const { data: premiumData, error: premiumError } = await supabase
          .from('policies')
          .select('premium_amount')
          .gte('sales_date', startDateStr)
          .lte('sales_date', endDateStr);
        
        if (premiumError) {
          console.error('Error fetching premium data:', premiumError);
          throw premiumError;
        }
        
        // Calculate total premiums
        const totalPremiums = premiumData.reduce((sum, policy) => {
          return sum + (typeof policy.premium_amount === 'string' 
            ? parseFloat(policy.premium_amount) 
            : (policy.premium_amount || 0));
        }, 0);
        
        // Calculate estimated commissions (15% of total premiums)
        const estimatedCommissions = Math.round(totalPremiums * 0.15);

        console.log(`Admin metrics - Fetched ${premiumData.length} policies for ${format(currentDate, 'MMMM yyyy')}`);
        console.log(`Total premiums: ${totalPremiums}, Estimated commissions: ${estimatedCommissions}`);

        return {
          usersCount: usersCount || 0,
          policiesCount: policiesCount || 0,
          totalPremiums,
          estimatedCommissions
        };
      } catch (error) {
        console.error('Error fetching admin metrics:', error);
        // If there's an error, return sensible defaults
        return {
          usersCount: 0,
          policiesCount: 0,
          totalPremiums: 0,
          estimatedCommissions: 0
        };
      }
    },
    refetchInterval: 60000, // Refetch every minute
  });
};
